/**
 * SegmentedControlHandler — 비동기 segmented-control 처리.
 *
 * admin.components.segmented-control (PHP) 가 렌더한 `.admin-segmented-control-form` 폼들을
 * fetch(async/await)로 가로채서 **페이지 새로고침 없이** 선택값을 저장한다.
 *
 * 패턴:
 *   1. radio change preventDefault (네이티브 submit 막기 위해 form submit 도 막음)
 *   2. $confirm 있으면 Modal.confirm → 거부 시 이전 값으로 롤백
 *   3. .is-loading 상태 + pointer-events 차단
 *   4. fetch (form method 기준 — POST 또는 _method=PATCH 등)
 *   5. 성공 → DOM 업데이트(is-selected 클래스) + Toast.success
 *   6. 오류 → 이전 값으로 롤백 + Toast.error / Modal.alert (criticalError 옵션)
 *
 * 서버 컨트롤러는 expectsJson()=true 일 때 { result, msg, value } 를 반환해야 한다.
 *
 * 사용:
 *   SegmentedControlHandler.init(root);          // root 내 모든 .admin-segmented-control-form 적용
 *   SegmentedControlHandler.init(root, options); // 커스텀 콜백 포함
 *
 * 카탈로그: docs/03-constants/components.md (SegmentedControlHandler)
 * 규칙: 상태변경 작업은 Toast 패턴 — docs/01-rules/must-check.md E-2
 */

import { Toast } from "../../utils/Toast";
import { Modal } from "../../utils/Modal";

export interface SegmentedControlHandlerOptions {
  /** 선택 성공 후 콜백. DOM 업데이트 뒤에 호출. */
  onSuccess?: (form: HTMLFormElement, result: SegmentedControlResult) => void;
  /** 오류 시 Toast.error 외에 추가 처리. */
  onError?: (form: HTMLFormElement, error: unknown) => void;
  /** true 면 오류 시 Toast.error 대신 Modal.alert (치명적 오류에 사용). */
  criticalError?: boolean;
}

export interface SegmentedControlResult {
  result: "success" | "fail";
  msg: string;
  value: string;
}

export const SegmentedControlHandler = {
  /**
   * root 내 모든 .admin-segmented-control-form 에 비동기 처리 바인딩.
   * 이미 data-sc-bound 가 있는 폼은 건너뜀.
   */
  init(root: HTMLElement | Document = document, options: SegmentedControlHandlerOptions = {}): void {
    root.querySelectorAll<HTMLFormElement>(".admin-segmented-control-form").forEach((form) => {
      if (form.dataset.scBound === "1") return;
      form.dataset.scBound = "1";
      this._bindForm(form, options);
    });
  },

  _bindForm(form: HTMLFormElement, options: SegmentedControlHandlerOptions): void {
    const group = form.querySelector<HTMLElement>(".admin-segmented-control");
    if (!group) return;

    // 네이티브 submit 차단 (radio change 만으로 동작)
    form.addEventListener("submit", (e) => e.preventDefault());

    // 폼의 radio 들에 change 리스너 부착
    form.querySelectorAll<HTMLInputElement>(".admin-segmented-control__input").forEach((input) => {
      input.addEventListener("change", async () => {
        if (!input.checked) return;

        const newValue = input.value;
        const prevValue = form.dataset.scPrevValue ?? this._currentCheckedValue(form, newValue);

        // 같은 값이면 무시
        if (prevValue === newValue && form.dataset.scInitialized === "1") return;
        form.dataset.scInitialized = "1";

        // --- 확인 모달
        const confirmMsg = form.dataset.confirm;
        if (confirmMsg) {
          const ok = await Modal.confirm(confirmMsg);
          if (!ok) {
            this._rollback(form, prevValue);
            return;
          }
        }

        // --- fetch payload (disabled 처리 전에 FormData 캡쳐 — disabled input 은 제외되므로)
        const fd = new FormData(form);

        // --- 로딩 상태
        group.classList.add("is-loading");
        form.querySelectorAll<HTMLInputElement>(".admin-segmented-control__input").forEach((i) => {
          i.disabled = true;
        });

        const restore = (): void => {
          group.classList.remove("is-loading");
          form.querySelectorAll<HTMLInputElement>(".admin-segmented-control__input").forEach((i) => {
            i.disabled = false;
          });
        };

        // --- fetch
        try {
          const res = await fetch(form.action, {
            method: (form.getAttribute("method") || "POST").toUpperCase(),
            headers: { Accept: "application/json" },
            body: fd,
            credentials: "same-origin",
          });

          let data: SegmentedControlResult;
          try {
            data = (await res.json()) as SegmentedControlResult;
          } catch {
            restore();
            this._rollback(form, prevValue);
            this._showError(form, "응답을 파싱할 수 없습니다.", options);
            return;
          }

          if (data.result !== "success") {
            restore();
            this._rollback(form, prevValue);
            this._showError(form, data.msg || "저장에 실패했습니다.", options);
            return;
          }

          // --- DOM 업데이트
          restore();
          this._applyState(form, data.value);
          form.dataset.scPrevValue = data.value;

          Toast.success(data.msg || "변경했습니다.");
          options.onSuccess?.(form, data);
        } catch (err: unknown) {
          restore();
          this._rollback(form, prevValue);
          const msg = err instanceof Error ? err.message : "네트워크 오류가 발생했습니다.";
          this._showError(form, msg, options);
          options.onError?.(form, err);
        }
      });
    });

    // 초기 prev 저장
    const initial = this._currentCheckedValue(form, "");
    form.dataset.scPrevValue = initial;
    form.dataset.scInitialized = "1";
  },

  _currentCheckedValue(form: HTMLFormElement, fallback: string): string {
    const checked = form.querySelector<HTMLInputElement>(".admin-segmented-control__input:checked");
    return checked?.value ?? fallback;
  },

  _applyState(form: HTMLFormElement, value: string): void {
    form.querySelectorAll<HTMLInputElement>(".admin-segmented-control__input").forEach((input) => {
      const isThis = input.value === value;
      input.checked = isThis;
      const label = form.querySelector<HTMLLabelElement>(`label[for="${input.id}"]`);
      if (label) {
        label.classList.toggle("is-selected", isThis);
      }
    });
  },

  _rollback(form: HTMLFormElement, prevValue: string): void {
    this._applyState(form, prevValue);
  },

  _showError(form: HTMLFormElement, msg: string, options: SegmentedControlHandlerOptions): void {
    if (options.criticalError) {
      void Modal.alert(msg);
    } else {
      Toast.error(msg);
    }
  },
};
